import { darken, lighten, Palette, PaletteColor, Theme, ThemeOptions } from '@mui/material';
import type { ChipPropsColorOverrides } from '@mui/material/Chip';

import { capitalDesignTokens } from '../../capital_design_tokens';

// tslint:disable:interface-name
declare module '@mui/material/Chip' {
  interface ChipPropsSizeOverrides {
    large: true;
  }
  interface ChipPropsVariantOverrides {
    minimal: true;
    strong: true;
  }

  interface ChipPropsColorOverrides {
    success: true;
    error: true;
    warning: true;
    inProgress: true;
    indigo: true;
    periwinkle: true;
    jade: true;
    port: true;
    rose: true;
    magenta: true;
    orange: true;
    terracotta: true;
    blue: true;
    cerulean: true;
  }
}

export const DEFAULT_CHIP_LABEL_PADDING = 1;
const FOREGROUND_COLOR_NUM = '700';
const BACKGROUND_STRONG_COLOR_NUM = '600';
const BACKGROUND_LIGHT_COLOR_NUM = '100';

const getTextColor = (isStrong: boolean, color: string) =>
  isStrong ? capitalDesignTokens.foundations.colors.white : color;

const getColorToken = (colorName: string, strength: string) =>
  capitalDesignTokens.foundations.colors[`${colorName}${strength}`];

interface ChipColorConfig {
  color: (isStrong: boolean) => string;
  background: (isStrong: boolean) => string;
  border: string;
  /** Palette family this chip reads in light mode. Omitted for the default and status chips, whose chip shades differ from the palette. */
  paletteFamily?: (palette: Palette) => PaletteColor | undefined;
}

/**
 * Complete color configuration for all chip variants
 */
const chipColors = {
  // Note: default colors are resolved dynamically via getChipColors(theme) to be theme-aware.
  // These fallback values are only used when theme is not provided (external callers).
  default: {
    color: (isStrong: boolean) => getTextColor(isStrong, capitalDesignTokens.foundations.colors.gray800),
    background: (isStrong: boolean) =>
      isStrong ? capitalDesignTokens.foundations.colors.gray700 : capitalDesignTokens.foundations.colors.gray100,
    border: capitalDesignTokens.foundations.colors.gray400
  },
  success: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('green', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('green', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('green', FOREGROUND_COLOR_NUM)
  },
  error: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('red', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('red', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('red', FOREGROUND_COLOR_NUM)
  },
  warning: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('yellow', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('yellow', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('yellow', FOREGROUND_COLOR_NUM)
  },
  inProgress: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('purple', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('purple', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('purple', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.inProgress
  },
  indigo: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('indigo', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('indigo', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('indigo', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.indigo
  },
  periwinkle: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('periwinkle', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('periwinkle', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('periwinkle', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.periwinkle
  },
  jade: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('jade', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('jade', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('jade', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.jade
  },
  port: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('port', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('port', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('port', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.port
  },
  rose: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('rose', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('rose', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('rose', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.rose
  },
  magenta: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('magenta', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('magenta', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('magenta', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.magenta
  },
  orange: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('orange', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('orange', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('orange', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.orange
  },
  terracotta: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('terracotta', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('terracotta', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('terracotta', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.terracotta
  },
  blue: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('blue', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('blue', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('blue', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.blue
  },
  cerulean: {
    color: (isStrong: boolean) => getTextColor(isStrong, getColorToken('cerulean', FOREGROUND_COLOR_NUM)),
    background: (isStrong: boolean) =>
      getColorToken('cerulean', isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken('cerulean', FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.cerulean
  }
} satisfies Record<'default' | keyof ChipPropsColorOverrides, ChipColorConfig>;

const isChipColor = (value: string): value is keyof typeof chipColors =>
  Object.prototype.hasOwnProperty.call(chipColors, value);

/**
 * Gets the color configuration for a chip based on its color and variant
 * Includes fallback for MUI v5 compatibility
 */
export const getChipColors = (ownerStateColor: string, ownerStateVariant: string, theme?: Theme) => {
  const isStrong = ownerStateVariant === 'strong';
  const isMinimal = ownerStateVariant === 'minimal';
  const color = ownerStateColor === 'secondary' || ownerStateColor === 'info' ? 'default' : ownerStateColor;

  // For default/neutral chips, use theme-aware palette tokens when theme is available
  if ((color === 'default' || !isChipColor(color)) && theme) {
    const textColor = isStrong ? theme.palette.common.white : theme.palette.text.secondary;
    const bgColor = isStrong ? theme.palette.action.disabled : theme.palette.action.hover;
    return {
      color: textColor,
      backgroundColor: isMinimal ? 'transparent' : bgColor,
      borderColor: theme.palette.divider
    };
  }

  const colorConfig: ChipColorConfig = isChipColor(color) ? chipColors[color] : chipColors.default;

  // Decorative families and inProgress read the theme palette, whose main / dark / light are the same 600 / 700 / 100
  // steps the token builder uses, so light mode renders identically. Dark mode keeps the token values until dark chip
  // colours are defined (the dark palette lacks most of these families). Status families have no paletteFamily
  // because their palette light shade (50) differs from the chip background (100).
  const family = theme?.palette.mode === 'light' ? colorConfig.paletteFamily?.(theme.palette) : undefined;
  // createTheme doesn't run custom palette keys through augmentColor, so a family given only `main` lacks the other
  // shades. Keep the token values then, rather than return undefined colours.
  if (family?.main && family.dark && family.light && family.contrastText) {
    return {
      color: isStrong ? family.contrastText : family.dark,
      backgroundColor: isMinimal ? 'transparent' : isStrong ? family.main : family.light,
      borderColor: family.dark
    };
  }

  return {
    color: colorConfig.color(isStrong),
    backgroundColor: isMinimal ? 'transparent' : colorConfig.background(isStrong),
    borderColor: colorConfig.border
  };
};

/**
 * Exporting for resuse in components that use Chip as a subcomponent.
 * Ex: Autocomplete selection tags
 */
export const getChipSizeStyles = (theme) => {
  return {
    // Extra small size is exclusively used for Autocomplete selection tags
    extraSmall: {
      fontSize: capitalDesignTokens.foundations.typography.fontSize.bodyXSmall,
      height: '20px'
    },
    small: {
      fontSize: capitalDesignTokens.componentFontSizes.small,
      height: `${capitalDesignTokens.componentSizes.small}px`,
      padding: theme.spacing(0, 0.5)
    },
    medium: {
      fontSize: capitalDesignTokens.componentFontSizes.medium,
      height: `${capitalDesignTokens.componentSizes.medium}px`,
      padding: theme.spacing(0, 0.5)
    },
    large: {
      fontSize: capitalDesignTokens.componentFontSizes.large,
      height: `${capitalDesignTokens.componentSizes.large}px`,
      padding: theme.spacing(0, 0.75)
    }
  };
};

/**
 * Exporting for resuse in Autocomplete limit tags
 */
export const getChipRootStyles = (colors: { color: string; backgroundColor: string; borderColor: string }) => {
  return {
    cursor: 'default',
    alignItems: 'center',
    fontWeight: capitalDesignTokens.foundations.typography.fontWeight.medium,
    borderRadius: '4px',
    color: colors.color,
    backgroundColor: colors.backgroundColor
  };
};

export const MuiChip: ThemeOptions['components']['MuiChip'] = {
  styleOverrides: {
    root: ({ theme, ownerState }) => {
      const chipSizeStyles = getChipSizeStyles(theme);
      const colors = getChipColors(ownerState.color || 'default', ownerState.variant, theme);
      const chipRootStyles = getChipRootStyles(colors);

      return {
        ...chipRootStyles,
        display: 'inline-flex',
        ...(ownerState.size === 'small' && chipSizeStyles.small),
        ...(ownerState.size === 'medium' && chipSizeStyles.medium),
        ...(ownerState.size === 'large' && chipSizeStyles.large),
        ...(ownerState.variant === 'minimal' && {
          padding: 0
        }),
        '&.Mui-focusVisible, &.MuiChip-clickable:hover, &:focus-visible': {
          ...(ownerState.variant !== 'minimal' &&
            ownerState.variant !== 'strong' && {
              backgroundColor: darken(colors.backgroundColor, 0.06)
            }),
          ...(ownerState.variant === 'minimal' && {
            color: lighten(colors.color, 0.2),
            backgroundColor: colors.backgroundColor
          }),
          ...(ownerState.variant === 'strong' && {
            backgroundColor: darken(colors.backgroundColor, 0.25)
          })
        },
        '&.Mui-disabled': {
          opacity: 0.6
        }
      };
    },
    outlined: ({ ownerState, theme }) => {
      const colors = getChipColors(ownerState.color || 'default', ownerState.variant, theme);
      return {
        borderColor: colors.borderColor,
        '&.Mui-focusVisible, &:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2
        }
      };
    },
    label: ({ theme, ownerState }) => {
      const hasLeadingElement = ownerState.avatar || ownerState.icon;
      const hasTrailingElement = ownerState.deleteIcon;
      const isMinimal = ownerState.variant === 'minimal';

      return {
        paddingLeft: theme.spacing(hasLeadingElement ? 1.25 : isMinimal ? 0 : DEFAULT_CHIP_LABEL_PADDING),
        paddingRight: theme.spacing(hasTrailingElement ? 1.25 : isMinimal ? 0 : DEFAULT_CHIP_LABEL_PADDING)
      };
    },
    icon: {
      fontSize: 'inherit',
      color: 'inherit'
    },
    deleteIcon: ({ ownerState, theme }) => {
      const colors = getChipColors(ownerState.color || 'default', ownerState.variant, theme);
      return {
        fontSize: 'inherit',
        color: 'inherit',
        '&:hover': {
          color: darken(colors.color, 0.3)
        }
      };
    },
    clickable: {
      '&:hover': {
        cursor: 'pointer'
      }
    }
  }
};
